<template>
  <div class="wizard-body w-[75%] flex-shrink-0 flex-grow-0 max-w-[75%]">
    <page-header :header-title="$t(title)" :header-content="$t(description)" />

    <!-- Step 1: name the inbox and create it -->
    <form v-if="!onboardingLink" class="row" @submit.prevent="createInbox()">
      <div class="medium-8 columns">
        <label :class="{ error: $v.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.HUB.CHANNEL_NAME.LABEL') }}
          <input
            v-model.trim="channelName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.HUB.CHANNEL_NAME.PLACEHOLDER')"
            @blur="$v.channelName.$touch"
          />
          <span v-if="$v.channelName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.HUB.CHANNEL_NAME.ERROR') }}
          </span>
        </label>
      </div>
      <div class="medium-12 columns">
        <woot-submit-button
          :loading="isCreating"
          :button-text="$t('INBOX_MGMT.ADD.HUB.SUBMIT_BUTTON')"
        />
      </div>
    </form>

    <!-- Step 2: show the onboarding link returned by the 99Hub -->
    <div v-else class="w-full">
      <p class="text-slate-700 dark:text-slate-200 mb-1">
        {{ $t('INBOX_MGMT.ADD.HUB.ONBOARDING_LINK.HELP') }}
      </p>
      <div class="onboarding-link">
        <a
          :href="onboardingLink"
          target="_blank"
          rel="noopener noreferrer"
          class="onboarding-link__url"
        >
          {{ onboardingLink }}
        </a>
        <button type="button" class="onboarding-link__copy" @click="copyLink">
          {{ $t('COMPONENTS.CODE.BUTTON_TEXT') }}
        </button>
      </div>
      <div class="mt-5">
        <woot-button @click="goToAgents">
          {{ $t('INBOX_MGMT.ADD.HUB.ONBOARDING_LINK.CONTINUE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import hubClient from 'dashboard/api/channel/hubClient';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';

export default {
  components: { PageHeader },
  mixins: [alertMixin],
  props: {
    // Backend channel type, e.g. 'hub_facebook_page'
    channelType: { type: String, required: true },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
  },
  data() {
    return {
      channelName: '',
      onboardingLink: '',
      inboxId: null,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    isCreating() {
      return this.uiFlags.isCreating;
    },
  },
  validations: {
    channelName: { required },
  },
  methods: {
    async createInbox() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.channelName,
          channel: { type: this.channelType },
        });
        this.inboxId = inbox.id;

        const {
          data: { onboarding_link: onboardingLink },
        } = await hubClient.getOnboardingLink(inbox.id);
        this.onboardingLink = onboardingLink;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Hub onboarding link failed', error?.response?.data);
        this.showAlert(this.$t('INBOX_MGMT.ADD.HUB.ERROR_MESSAGE'));
      }
    },
    async copyLink() {
      await copyTextToClipboard(this.onboardingLink);
      this.showAlert(this.$t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
    },
    goToAgents() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.inboxId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.onboarding-link {
  @apply flex items-center justify-between gap-3 mt-1 py-2 pl-4 pr-2 rounded-xl
    border border-solid border-slate-100 dark:border-slate-700
    bg-slate-25 dark:bg-slate-800;
}

.onboarding-link__url {
  @apply flex-1 min-w-0 text-sm font-medium break-all
    text-woot-600 dark:text-woot-400 hover:underline;
}

.onboarding-link__copy {
  @apply flex-shrink-0 cursor-pointer rounded-lg border-0 bg-transparent
    py-1.5 px-3 text-sm font-medium text-woot-600 dark:text-woot-400
    transition-colors duration-150;

  &:hover {
    @apply bg-woot-50 dark:bg-woot-700;
  }

  &:focus {
    @apply outline-none;
  }
}
</style>
